import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { Input } from "../../components/ui/input";
import { useGetLookup } from "../../api/admin";
import { useTablePrint } from "../../hooks/useTablePrint";
import { Icons } from "../../assets/images/icons";
import { AppButton } from "../../components/ui/AppButton";
import { ExportButton } from "../../components/ui/ExportButton";
import { DataTable } from "../../components/ui/data-table";
import { Lookup } from "../../types/lookup";
import { getLookupTypeLabel } from "../../util/lookup-type";

function LookupPage() {
  /** State */
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  /** API */
  const { data: lookups, isLoading } = useGetLookup({
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });

  const { handlePrint } = useTablePrint();

  /** Transform lookups for table */
  const transformedLookups = useMemo((): Lookup[] => {
    if (!lookups?.data?.content) return [];
    return lookups.data.content.map((item: Lookup) => ({
      id: item.id,
      lookupType: item.lookupType || "N/A",
      lookupValue: item.lookupValue,
      lookupDescription: item.lookupDescription,
    }));
  }, [lookups]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  /** Columns */
  const lookupColumns = [
    {
      key: "lookupType",
      title: "Lookup Type",
      dataIndex: "lookupType",
      align: "left" as const,
      render: (value: number) => <div>{getLookupTypeLabel(value)}</div>,
    },
    {
      key: "lookupValue",
      title: "Lookup Value",
      dataIndex: "lookupValue",
      align: "left" as const,
    },
    {
      key: "lookupDescription",
      title: "Description",
      dataIndex: "lookupDescription",
      align: "left" as const,
    },
  ];

  return (
    <div
      className="bg-transparent lg:bg-white"
      style={{ borderRadius: 8.5, marginTop: 25 }}
    >
      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search lookups"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(lookupColumns, transformedLookups, "Lookup Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={lookupColumns}
            data={transformedLookups}
            fileName="lookups"
            title="Lookup Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: lookups?.data?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      {/** Desktop Table */}
      <div className="hidden md:block">
        <DataTable<Lookup>
          data={transformedLookups}
          columns={lookupColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: lookups?.data?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No lookups found"
          rowKey="id"
        />
      </div>
    </div>
  );
}

export default WithAuth(LookupPage);
